import type { FC } from "react";

import { InputGroup, InputGroupInput } from "@/ui/components/form/InputGroup";
import { Column, Container, Grid } from "@/ui/components/layout";
import { Button } from "@/ui/components/primitives/Button";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";
import { cn } from "@/ui/lib";
import { useTranslations } from "next-intl";

import {
  NEWSLETTER_SIGNUP_DEFAULT_INPUTS,
  NEWSLETTER_SIGNUP_DEFAULTS,
  type TNewsletterSignupProps,
} from "./NewsletterSignup.config";

const makeNewsletterSignupInputKey = (
  inputProps: NonNullable<TNewsletterSignupProps["inputs"]>[number],
  index: number,
) => inputProps.id ?? inputProps.name ?? inputProps.placeholder?.toString() ?? index;

const NEWSLETTER_SIGNUP_INPUT_PLACEHOLDER_KEYS = {
  newsletter_email: "inputs.email.placeholder",
} as const;

export const NewsletterSignup: FC<TNewsletterSignupProps> = (
  {
    "data-component": component = NEWSLETTER_SIGNUP_DEFAULTS["data-component"],
    title,
    body,
    button,
    inputs = NEWSLETTER_SIGNUP_DEFAULT_INPUTS,
    className,
    ...props
  },
) => {
  const t = useTranslations("Components.Marketing.NewsletterSignUp");

  const resolvedButton = {
    ...NEWSLETTER_SIGNUP_DEFAULTS.button,
    ...button,
    children: button?.children ?? t("button.children"),
  };

  const resolvedInputs = inputs.map((inputProps) => {
    const placeholderKey = inputProps.name
      ? NEWSLETTER_SIGNUP_INPUT_PLACEHOLDER_KEYS[
        inputProps.name as keyof typeof NEWSLETTER_SIGNUP_INPUT_PLACEHOLDER_KEYS
      ]
      : undefined;

    return {
      ...inputProps,
      placeholder: inputProps.placeholder ?? (placeholderKey ? t(placeholderKey) : undefined),
    };
  });

  return (
    <form
      {...props}
      data-component={component}
      className={cn("w-full bg-sys-black-850", className)}
    >
      <Container className="flex flex-col items-start gap-4 px-3 pb-4 pt-8">
        <Grid gap="1.5">
          <Column className="flex-col items-start gap-1">
            <TextSubtitle
              tag="span"
              size={3}
              isResponsive={false}
              className="!text-sys-chartreuse-600"
            >
              { title ?? t("title") }
            </TextSubtitle>
            <TextSubtitle
              tag="h2"
              size={2}
              isResponsive={false}
              className="!text-sys-white"
            >
              { body ?? t("body") }
            </TextSubtitle>
          </Column>
        </Grid>
        <div
          data-component="NewsletterSignupControls"
          className="flex w-full max-w-[64rem] flex-col gap-4 md:max-w-[50%] md:flex-row md:items-center"
        >
          { resolvedInputs.map((inputProps, index) => {
            const {
              className: inputClassName,
              ...restInputProps
            } = inputProps;

            return (
              <Column
                key={makeNewsletterSignupInputKey(inputProps, index)}
                className="w-full flex-1"
              >
                <InputGroup className="min-h-9 border-strong">
                  <InputGroupInput
                    {...restInputProps}
                    className={inputClassName}
                  />
                </InputGroup>
              </Column>
            );
          }) }
          <Column className="shrink-0">
            <Button type="submit" variant="secondary" size="lg" state={resolvedButton.state}>
              { resolvedButton.children }
            </Button>
          </Column>
        </div>
      </Container>
    </form>
  );
};

NewsletterSignup.displayName = "NewsletterSignup";
